import {
  customInstanceWithHeader,
  type ResponseWithHeaders,
} from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodePartnerAdminsClientsKey,
  getGetApiV1ThreeLetterCodePartnerAdminsClientsUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type {
  GetApiV1ThreeLetterCodePartnerAdminsClients200One,
  GetApiV1ThreeLetterCodePartnerAdminsClientsParams,
} from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import type { KeyedMutator } from 'swr';

/**
 * 未与信件数取得用の fetcher
 *
 * - Response Header の `total-count` から未与信件数を取得する
 * - body（clients 配列）は使用しない（与信未完了の利用企業一覧の中身は不要）
 * - ページング目的ではなく件数取得のみが目的のため `total-count` のみを使用する
 */
const fetcher = (
  threeLetterCode: string,
  apiParams: GetApiV1ThreeLetterCodePartnerAdminsClientsParams
) =>
  customInstanceWithHeader<GetApiV1ThreeLetterCodePartnerAdminsClients200One>(
    getGetApiV1ThreeLetterCodePartnerAdminsClientsUrl(threeLetterCode, apiParams),
    { method: 'GET' }
  );

type Args = {
  /** パートナーの 3 文字コード */
  threeLetterCode: string;
  /**
   * 与信評価フラグが有効な場合のみ fetch を発火するかどうか
   *
   * false のとき SWR のキーが null になり fetch をスキップする（条件付きフェッチ）
   */
  enabled: boolean;
};

type IUseGetHasUngrantedCredit = (args: Args) => {
  /** 未与信件数取得 API レスポンス本体。enabled=false のとき undefined */
  data: GetApiV1ThreeLetterCodePartnerAdminsClients200One | undefined;
  /** 総ページ数。enabled=false のとき 0 */
  totalPage: number;
  /** 未与信件数（total-count ヘッダーから解釈済み）。enabled=false のとき 0 */
  totalCount: number;
  /** キャッシュを再検証して未与信件数を最新化する */
  mutate: KeyedMutator<ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsClients200One>>;
};

/** 与信状態「未完了」で固定するクエリパラメータ */
const UNGRANTED_CREDIT_PARAMS: GetApiV1ThreeLetterCodePartnerAdminsClientsParams = {
  credit_granted: 0,
  enabled: 1,
};

/**
 * 未与信件数取得フック
 *
 * - credit_granted=0 かつ enabled=1 で一覧 API を呼び出す
 * - enabled=false のとき（creditEvaluationEnabled が無効のとき）は fetch をスキップする
 * - レスポンスヘッダーの解釈（total-count / total-pages → number）を hook 内で完結させる
 * - grantCredit 成功後に mutate() を呼ぶことで未与信件数を最新化できる
 */
export const useGetHasUngrantedCredit: IUseGetHasUngrantedCredit = ({
  threeLetterCode,
  enabled,
}) => {
  const swrKey = enabled
    ? getGetApiV1ThreeLetterCodePartnerAdminsClientsKey(threeLetterCode, UNGRANTED_CREDIT_PARAMS)
    : null;

  const { data, mutate } = useFetch<
    ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsClients200One>
  >(swrKey, () => fetcher(threeLetterCode, UNGRANTED_CREDIT_PARAMS), { suspense: true });

  const totalPage = data ? Number(data.headers['total-pages']) || 0 : 0;
  const totalCount = data ? Number(data.headers['total-count']) || 0 : 0;

  return { data: data?.data, totalPage, totalCount, mutate };
};
